import { useCallback, useEffect, useRef, useState } from 'react';

import { useUtilityContext } from '@change-corgi/core-react/utilityContext';

import { useWindowMessage } from '../useWindowMessage';

/**
 * `pending` until the child handshakes, then terminal: `ready` once it does, or `timedout`
 * if it never does. Consumers keep the iframe hidden until `ready`.
 */
export type SandboxReadinessStatus = 'pending' | 'ready' | 'timedout';

/**
 * How long the host waits for the child's `ready` handshake before treating the iframe as
 * failed to load. Tuned for shell readiness (a CDN-served document), not for any in-child
 * async work (e.g. an LLM response) — the child owns that with its own UI.
 */
export const SANDBOX_READY_TIMEOUT_MS = 8_000;

type UseSandboxReadinessOptions = {
	childOrigin: string;
	/**
	 * Side effect for a load timeout — the child never sent `ready` in time. The channel
	 * reports the timeout itself; this only decides what *else* happens (e.g. fall back to a
	 * non-iframe step). Swappable — omit it and the timeout is reported only.
	 */
	onLoadTimeout?: () => void;
	timeoutMs?: number;
};

/**
 * Detects a child iframe that never becomes interactive. A cross-origin iframe gives the host
 * no reliable native "failed to load" signal (`onerror` rarely fires for navigation failures;
 * `onload` fires even for error pages), so readiness is a handshake: the child posts
 * `{ type: 'ready' }` once its shell is interactive. If that doesn't arrive within `timeoutMs`
 * of mount, the host treats it as a load failure. The outcome is terminal — whichever of
 * `ready` / timeout happens first wins, and a late `ready` is ignored.
 */
export function useSandboxReadiness({
	childOrigin,
	onLoadTimeout,
	timeoutMs = SANDBOX_READY_TIMEOUT_MS,
}: UseSandboxReadinessOptions): { status: SandboxReadinessStatus } {
	const { errorReporter } = useUtilityContext();

	const [status, setStatus] = useState<SandboxReadinessStatus>('pending');
	// terminal-outcome guard: whichever of ready / timeout happens first wins. Kept in a ref
	// (not derived from `status`) so side effects stay out of the state updater and a late
	// signal is a no-op regardless of render timing.
	const settledRef = useRef(false);
	// latest handler in a ref so the timer effect arms once and does not re-arm as the
	// handler's identity changes between renders
	const onLoadTimeoutRef = useRef(onLoadTimeout);
	useEffect(() => {
		onLoadTimeoutRef.current = onLoadTimeout;
	});

	const handleReady = useCallback(() => {
		if (settledRef.current) return;
		settledRef.current = true;
		setStatus('ready');
	}, []);

	useWindowMessage('ready', childOrigin, handleReady);

	useEffect(() => {
		const timer = setTimeout(() => {
			if (settledRef.current) return;
			settledRef.current = true;
			void errorReporter.report(new Error(`SAP: sandbox failed to load (no ready within ${timeoutMs}ms)`));
			onLoadTimeoutRef.current?.();
			setStatus('timedout');
		}, timeoutMs);

		return () => clearTimeout(timer);
	}, [timeoutMs, errorReporter]);

	return { status };
}
